/**
 * Reads the user-facing message out of an API error body.
 *
 * The upload endpoints answer with two error shapes. The HTTP layer writes the
 * wrapped envelope:
 *
 *     {"error": {"http_code": 403, "type": "Forbidden", "code": "album_full",
 *                "message": "Album is full"}}
 *
 * while a rejected upload job publishes the same fields bare, without the
 * wrapper. Both carry a message the backend has already localized for the
 * requesting user, so it is the message to show rather than anything the client
 * can compose for itself.
 *
 * Markup is stripped from the message. Alert rendering deliberately carries the
 * `<a>`, `<b>`, and `<br>` that alert translations use, so a message coming back
 * from an upload has to arrive without markup of its own to stay text.
 */

/** Matches an HTML tag, so an upstream message contributes only its text. */
const TAG_PATTERN = /<[^>]*>/g;

/**
 * @param {string} [responseText] Raw response body
 * @returns {string|null} The message the body carries, or null when it carries none
 */
export function apiErrorMessage(responseText) {
    if (!responseText) {
        return null;
    }

    let body;
    try {
        body = JSON.parse(responseText);
    } catch {
        return null;
    }

    if (!body || typeof body !== 'object') {
        return null;
    }

    // The bare shape is only recognized alongside one of the fields that mark a
    // body as an API error, so an unrelated payload that happens to carry a
    // "message" is not mistaken for a rejection.
    const isBareApiError = body.http_code !== undefined || body.code !== undefined;
    const message = body.error?.message ?? (isBareApiError ? body.message : undefined);

    if (typeof message !== 'string') {
        return null;
    }

    const text = message.replace(TAG_PATTERN, '').trim();

    return text === '' ? null : text;
}
